import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { HSE_ACTION_KINDS, NC_CLASSES, ROOT_CAUSE_MIN_LENGTH } from './constants.js';
import { isActionOverdue } from './status.js';

/**
 * Ofisin doğrudan açtığı şirket uygunsuzluğu (ADR 0062 §2): ISM Kodu 9 (uygunsuzluğun raporlanması,
 * düzeltici faaliyet) ve 12 (şirketin iç denetimi ofisi de kapsar). Kayıt şirket verisidir, yalnız
 * bulutta yazılır; gemi kaydı (`hse_reports`) değildir. Hata iletileri koddur.
 */

export const COMPANY_NC_PREFIX = 'CNC';

/** Bulgunun kaynağı (ADR 0062 §2.3). */
export const COMPANY_NC_SOURCES = [
  'internal_audit',
  'external_audit',
  'management_review',
  'flag_inspection',
  'class_survey',
  'vetting',
  'customer_complaint',
  'self_detected',
  'other',
] as const;
export type CompanyNcSource = (typeof COMPANY_NC_SOURCES)[number];

/** Ofis bölümleri. */
export const COMPANY_NC_DEPARTMENTS = [
  'management',
  'technical',
  'marine',
  'crewing',
  'hse',
  'purchasing',
  'accounts',
  'it',
  'office',
  'other',
] as const;

/** Görünen durum (türetilir; ADR 0062 §2.4). */
export const COMPANY_NC_STATUSES = [
  'open',
  'action_assigned',
  'in_progress',
  'pending_verification',
  'closed',
  'reopened',
] as const;
export type CompanyNcStatus = (typeof COMPANY_NC_STATUSES)[number];

export const COMPANY_NC_EVENT_KINDS = ['closed', 'reopened'] as const;
export type CompanyNcEventKind = (typeof COMPANY_NC_EVENT_KINDS)[number];

const collapse = (value: string) => value.normalize('NFC').trim();
const text = (max: number) =>
  z
    .string()
    .nullish()
    .transform((value) => {
      if (value === null || value === undefined) return null;
      const trimmed = collapse(value);
      return trimmed === '' ? null : trimmed;
    })
    .pipe(z.string().max(max, 'TOO_LONG').nullable());
const requiredText = (max: number) =>
  z
    .string({ message: 'REQUIRED' })
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const dayRe = /^\d{4}-\d{2}-\d{2}$/;
const day = z.string().regex(dayRe, 'INVALID_DATE').nullish().default(null);

const ncSchema = z.object({
  title: requiredText(200),
  description: requiredText(8000),
  source: z.enum(COMPANY_NC_SOURCES, { message: 'REQUIRED' }),
  ncClass: z.enum(NC_CLASSES, { message: 'REQUIRED' }),
  smsReference: text(300),
  department: z.enum(COMPANY_NC_DEPARTMENTS, { message: 'INVALID' }).nullish().default(null),
  detectedOn: z.string({ message: 'REQUIRED' }).regex(dayRe, 'INVALID_DATE'),
  detectedBy: requiredText(200),
  vesselId: z.uuid({ message: 'INVALID_ID' }).nullish().default(null),
  immediateAction: text(4000),
  rootCause: text(4000),
  verificationNote: text(4000),
  responsible: text(200),
  targetDate: day,
});
export type CompanyNcInput = z.infer<typeof ncSchema>;

function issuesOf(error: z.ZodError): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code:
      issue.code === 'invalid_type' && (issue.input === undefined || issue.input === null)
        ? 'REQUIRED'
        : issue.message,
  }));
}

/** Kaydı doğrular ve biçimler; açılışta zorunlular istenir (taslak yok: ofis kaydı açılınca raporlanmıştır). */
export function parseCompanyNc(input: unknown): ParseResult<CompanyNcInput> {
  const result = ncSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issuesOf(result.error) };
}

const actionSchema = z.object({
  kind: z.enum(HSE_ACTION_KINDS, { message: 'REQUIRED' }),
  description: requiredText(2000),
  responsible: requiredText(200),
  responsibleUserId: z.uuid({ message: 'INVALID_ID' }).nullish().default(null),
  dueDate: z.string({ message: 'REQUIRED' }).regex(dayRe, 'INVALID_DATE'),
});
export type CompanyNcActionInput = z.infer<typeof actionSchema>;

export function parseCompanyNcAction(input: unknown): ParseResult<CompanyNcActionInput> {
  const result = actionSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issuesOf(result.error) };
}

/** Numara: `CNC-2026-001` (tespit yılı, şirket başına boşluksuz). */
export function companyNcNo(year: number, seq: number): string {
  return `${COMPANY_NC_PREFIX}-${year}-${String(seq).padStart(3, '0')}`;
}

/**
 * Görünen durum (ADR 0062 §2.4): son olay kapanışsa `closed`; yeniden açıldıysa ve o andan sonra
 * faaliyet açılmadıysa `reopened`; değilse canlı faaliyetlerden.
 */
export function deriveCompanyNcStatus(input: {
  actions: readonly { status: string; createdAt: string }[];
  events: readonly { kind: string; at: string }[];
}): CompanyNcStatus {
  // Kararlı sıralama: aynı andaki olaylar verilen sırayla (çağıran kimlik sırasıyla verir)
  const last = [...input.events].sort((a, b) => a.at.localeCompare(b.at)).at(-1);
  if (last?.kind === 'closed') return 'closed';
  const live = input.actions.filter((a) => a.status !== 'cancelled');
  if (last?.kind === 'reopened' && !live.some((a) => a.createdAt > last.at)) return 'reopened';
  if (live.length === 0) return 'open';
  if (live.every((a) => a.status === 'completed')) return 'pending_verification';
  if (live.every((a) => a.status === 'planned')) return 'action_assigned';
  return 'in_progress';
}

export function isCompanyNcOverdue(
  nc: {
    status: CompanyNcStatus;
    targetDate: string | null;
    actions: readonly { status: string; dueDate: string | null }[];
  },
  today: string,
): boolean {
  if (nc.status === 'closed') return false;
  if (nc.targetDate !== null && nc.targetDate < today) return true;
  return nc.actions.some((a) => isActionOverdue(a, today));
}

export interface CompanyNcKpis {
  total: number;
  open: number;
  pendingVerification: number;
  overdue: number;
  closed: number;
}

export function companyNcKpis(
  items: readonly { status: CompanyNcStatus; overdue: boolean }[],
): CompanyNcKpis {
  return {
    total: items.length,
    open: items.filter((i) => i.status !== 'closed').length,
    pendingVerification: items.filter((i) => i.status === 'pending_verification').length,
    overdue: items.filter((i) => i.overdue).length,
    closed: items.filter((i) => i.status === 'closed').length,
  };
}

const filled = (value: unknown, min = 1) => typeof value === 'string' && value.trim().length >= min;

/**
 * Kapanışın eksikleri (ADR 0062 §2.5): kök neden (en az 20 karakter), canlı faaliyet, hepsi
 * tamamlanmış, en az bir düzeltici ya da önleyici [ISM 9.2], etkinlik doğrulaması.
 */
export function companyNcClosureGaps(
  nc: { rootCause: string | null; verificationNote: string | null },
  actions: readonly { kind: string; status: string }[],
): string[] {
  const gaps: string[] = [];
  if (!filled(nc.rootCause, ROOT_CAUSE_MIN_LENGTH)) gaps.push('ROOT_CAUSE');
  const live = actions.filter((a) => a.status !== 'cancelled');
  if (live.length === 0) gaps.push('NO_ACTIONS');
  else if (live.some((a) => a.status !== 'completed')) gaps.push('ACTIONS_OPEN');
  else if (!live.some((a) => a.kind === 'corrective' || a.kind === 'preventive')) {
    gaps.push('CORRECTIVE_ACTION');
  }
  if (!filled(nc.verificationNote)) gaps.push('VERIFICATION_NOTE');
  return gaps;
}

/** Kapanış: açan kişi kapatamaz (dört göz; ADR 0049 §4 emsali), eksik yok. */
export function assertCompanyNcClosable(
  nc: { rootCause: string | null; verificationNote: string | null },
  actions: readonly { kind: string; status: string }[],
  people: { openedBy: string; closer: string },
): void {
  if (people.openedBy === people.closer) throw new DomainError('COMPANY_NC_SELF_CLOSE');
  const gaps = companyNcClosureGaps(nc, actions);
  if (gaps.length > 0) {
    throw new DomainError(
      'COMPANY_NC_NOT_CLOSABLE',
      `COMPANY_NC_NOT_CLOSABLE ${gaps.join(',')}`,
      gaps.join(','),
    );
  }
}
